const DISMISSED_UPDATE_STORAGE_KEY = "grain-saas.dismissed-update-id";

export function appendRecentProductUpdateDismissal(values, id) {
  return [...normalizeDismissedUpdateIds(values).filter(value => value !== id), id].slice(-200);
}

const EMPTY_DISMISSED_UPDATES_SNAPSHOT = "[]";

let memoryDismissedUpdateIdsSnapshot = EMPTY_DISMISSED_UPDATES_SNAPSHOT;

const dismissedUpdateListeners = new Set();

function normalizeDismissedUpdateId(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function normalizeDismissedUpdateIds(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedIds = [];
  const seenIds = new Set();

  values.forEach((value) => {
    const normalizedValue = normalizeDismissedUpdateId(value);

    if (!normalizedValue || seenIds.has(normalizedValue)) {
      return;
    }

    seenIds.add(normalizedValue);
    normalizedIds.push(normalizedValue);
  });

  return normalizedIds;
}

export function parseDismissedUpdateIdsSnapshot(value) {
  if (typeof value !== "string") {
    return [];
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return [];
  }

  if (normalizedValue.startsWith("[")) {
    try {
      return normalizeDismissedUpdateIds(JSON.parse(normalizedValue));
    } catch {
      return [];
    }
  }

  const normalizedDismissedUpdateId = normalizeDismissedUpdateId(normalizedValue);
  return normalizedDismissedUpdateId ? [normalizedDismissedUpdateId] : [];
}

export function serializeDismissedUpdateIds(values) {
  const normalizedIds = normalizeDismissedUpdateIds(values);

  if (!normalizedIds.length) {
    return EMPTY_DISMISSED_UPDATES_SNAPSHOT;
  }

  return JSON.stringify(normalizedIds);
}

function getLocalStorageSafely() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function emitDismissedUpdateIdChange() {
  dismissedUpdateListeners.forEach((listener) => {
    listener();
  });
}

function getUpdateTimestamp(update) {
  const normalizedDate = typeof update?.date === "string" ? update.date.trim() : "";
  const timestamp = Date.parse(normalizedDate);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function getDismissedUpdateIdsSnapshot() {
  const localStorage = getLocalStorageSafely();

  if (!localStorage) {
    return memoryDismissedUpdateIdsSnapshot;
  }

  try {
    return serializeDismissedUpdateIds(parseDismissedUpdateIdsSnapshot(localStorage.getItem(DISMISSED_UPDATE_STORAGE_KEY)));
  } catch {
    return memoryDismissedUpdateIdsSnapshot;
  }
}

export function getDismissedUpdateIds() {
  return parseDismissedUpdateIdsSnapshot(getDismissedUpdateIdsSnapshot());
}

export function dismissUpdateById(updateId) {
  const normalizedUpdateId = normalizeDismissedUpdateId(updateId);

  if (!normalizedUpdateId) {
    return;
  }

  const nextDismissedUpdateIds = new Set(getDismissedUpdateIds());
  nextDismissedUpdateIds.add(normalizedUpdateId);
  setDismissedUpdateIds([...nextDismissedUpdateIds]);
}

export function setDismissedUpdateIds(values) {
  const normalizedSnapshot = serializeDismissedUpdateIds(values);
  const normalizedIds = parseDismissedUpdateIdsSnapshot(normalizedSnapshot);
  const localStorage = getLocalStorageSafely();

  memoryDismissedUpdateIdsSnapshot = normalizedSnapshot;

  if (localStorage) {
    try {
      if (normalizedIds.length > 0) {
        localStorage.setItem(DISMISSED_UPDATE_STORAGE_KEY, normalizedSnapshot);
      } else {
        localStorage.removeItem(DISMISSED_UPDATE_STORAGE_KEY);
      }
    } catch {
      // Fallback silencieux: l'UI continue avec le cache mémoire du module.
    }
  }

  emitDismissedUpdateIdChange();
}

export function subscribeDismissedUpdateId(onStoreChange) {
  if (typeof onStoreChange !== "function") {
    return () => {};
  }

  dismissedUpdateListeners.add(onStoreChange);

  if (typeof window === "undefined") {
    return () => {
      dismissedUpdateListeners.delete(onStoreChange);
    };
  }

  const handleStorage = (event) => {
    if (!event || event.key === null || event.key === DISMISSED_UPDATE_STORAGE_KEY) {
      onStoreChange();
    }
  };

  window.addEventListener("storage", handleStorage);

  return () => {
    dismissedUpdateListeners.delete(onStoreChange);
    window.removeEventListener("storage", handleStorage);
  };
}

export function getLatestAnnouncedUpdate(updates = []) {
  if (!Array.isArray(updates) || updates.length === 0) {
    return null;
  }

  return updates
    .filter((update) => update?.announce === true)
    .slice()
    .sort((leftUpdate, rightUpdate) => getUpdateTimestamp(rightUpdate) - getUpdateTimestamp(leftUpdate))[0] ?? null;
}

export function getVisibleAnnouncedUpdates(updates = [], dismissedUpdateIds = []) {
  if (!Array.isArray(updates) || updates.length === 0) {
    return [];
  }

  const dismissedUpdateIdsSet = new Set(normalizeDismissedUpdateIds(dismissedUpdateIds));

  return updates.filter((update) => {
    const updateId = normalizeDismissedUpdateId(update?.id);
    return Boolean(updateId) && update?.announce === true && !dismissedUpdateIdsSet.has(updateId);
  });
}

export function isUpdateUnread(update, dismissedUpdateIds = []) {
  const updateId = normalizeDismissedUpdateId(update?.id);
  const dismissedUpdateIdsSet = new Set(normalizeDismissedUpdateIds(dismissedUpdateIds));

  if (!updateId || update?.announce !== true) {
    return false;
  }

  return !dismissedUpdateIdsSet.has(updateId);
}

export { DISMISSED_UPDATE_STORAGE_KEY };
